import React, { useContext, useState, useEffect } from 'react';
import { Modal, Button, Badge, Row, Col, Pagination, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faMoneyBillWave, faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';

const getTipoIcon = (tipo) => {
    if (!tipo) return { icon: faFileInvoice, color: '#6c757d', label: '' };
    const t = tipo.toString().toLowerCase();
    if (t.includes('ing') || t === 'i' || t.includes('ingreso')) return { icon: faArrowUp, color: '#28a745', label: 'Ingreso' };
    if (t.includes('egr') || t === 'e' || t.includes('egreso')) return { icon: faArrowDown, color: '#dc3545', label: 'Egreso' };
    if (t.includes('pag') || t === 'p' || t.includes('pago')) return { icon: faMoneyBillWave, color: '#0d6efd', label: 'Pago' };
    return { icon: faFileInvoice, color: '#6c757d', label: tipo };
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SelectFacturaPPDModal = ({
    show,
    onHide,
    onConfirm,
    title = 'Seleccionar factura',
    rfcEmisor = '',
    rfcReceptor = ''
}) => {
    const { config } = useContext(AppContext);
    const isDark = config.isDark;
    const { token } = useToken();

    const [facturas, setFacturas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFactura, setSelectedFactura] = useState(null);
    const [apiMessage, setApiMessage] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSearch, setTempSearch] = useState('');
    const itemsPerPage = 5;

    useEffect(() => {
        if (show) {
            fetchFacturas();
        } else if (!show) {
            setFacturas([]);
            setCurrentPage(1);
            setSelectedFactura(null);
            setApiMessage('');
            setTotalRecords(0);
            setSearchQuery('');
            setTempSearch('');
        }
    }, [show, rfcEmisor, rfcReceptor, currentPage, searchQuery]);

    const fetchFacturas = async () => {
        setIsLoading(true);
        setApiMessage('');
        try {
            const resFacturas = await fetch('/api/contabilidad/facturas/FormularioFactura/ObtenerFacturasPPDPorRFC', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vRfcReceptor: rfcReceptor,
                    vRfcEmisor: rfcEmisor,
                    vRazonSocial: searchQuery,
                    PageNumber: currentPage,
                    PageSize: itemsPerPage
                })
            });
            if (resFacturas.ok) {
                const dataFacturas = await resFacturas.json();
                if (dataFacturas.vMessage) {
                    setApiMessage(dataFacturas.vMessage);
                }
                if (dataFacturas.iResponseType === 1) {
                    let parsedDetalles = [];
                    if (typeof dataFacturas.detalles === 'string') {
                        parsedDetalles = dataFacturas.detalles ? JSON.parse(dataFacturas.detalles) : [];
                    } else if (Array.isArray(dataFacturas.detalles)) {
                        parsedDetalles = dataFacturas.detalles;
                    }

                    setFacturas(parsedDetalles);
                    setTotalRecords(dataFacturas.totalRecords || 0);
                } else {
                    setFacturas([]);
                    setTotalRecords(0);
                }
            }
        } catch (e) {
            console.error("Error fetching facturas PPD:", e);
            setApiMessage("Error al conectar con el servidor.");
        } finally {
            setIsLoading(false);
        }
    };

    const hasFacturas = facturas && facturas.length > 0;

    const currentItems = facturas; // Ya vienen paginados desde el backend
    const totalPages = Math.ceil(totalRecords / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = () => {
        setSearchQuery(tempSearch);
        setCurrentPage(1);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const renderPaginationItems = () => {
        let items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} />);
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(<Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />);
        }

        return items;
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" dialogClassName={isDark ? 'modal-dark' : ''}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="alert alert-info py-2 mb-3 fs--1">
                    Buscando facturas PPD emitidas por <strong>{rfcEmisor || '...'}</strong> y recibidas por <strong>{rfcReceptor || 'Todos (RFC Opcional)'}</strong>
                </div>
                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Buscar por razón social..."
                        value={tempSearch}
                        onChange={(e) => setTempSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Button variant="outline-secondary" onClick={handleSearch}>
                        Buscar
                    </Button>
                </InputGroup>
                {isLoading ? (
                    <div className="d-flex flex-column gap-2 mb-2">
                        {[...Array(5)].map((_, idx) => (
                            <div key={idx} className="p-2 border rounded shadow-sm placeholder-glow" style={{ backgroundColor: isDark ? '#121e2d' : '#ffffff', borderColor: isDark ? '#2b3035' : '#e9ecef' }}>
                                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
                                    
                                    <div className="d-flex align-items-center w-100">
                                        <div className="rounded-circle me-2 flex-shrink-0 placeholder" style={{ width: '32px', height: '32px' }}></div>
                                        <div className="d-flex flex-column justify-content-center w-100">
                                            <div className="mb-1 w-50">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                            <div className="mb-1 w-75">
                                                <span className="placeholder col-12"></span>
                                            </div>
                                            <div className="w-50">
                                                <span className="placeholder col-6"></span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap flex-sm-nowrap gap-2 justify-content-between text-end ms-lg-auto bg-light rounded p-1 px-2" 
                                         style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2) !important' : '#f8f9fa !important', minWidth: '280px' }}>
                                        <div className="d-flex flex-column px-1 flex-grow-1">
                                            <span className="placeholder col-8 mb-1"></span>
                                            <span className="placeholder col-12"></span>
                                        </div>
                                        <div className="d-flex flex-column px-1 border-start flex-grow-1">
                                            <span className="placeholder col-8 mb-1"></span>
                                            <span className="placeholder col-12"></span>
                                        </div>
                                        <div className="d-flex flex-column px-1 border-start flex-grow-1">
                                            <span className="placeholder col-8 mb-1"></span>
                                            <span className="placeholder col-12"></span>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                ) : !hasFacturas ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faFileInvoice} size="3x" className="text-muted mb-3" />
                        <p className="text-muted">{apiMessage || 'No hay facturas disponibles'}</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2 mb-2">
                        {currentItems.map((f, idx) => {
                            const uuid = f?.vUUIDFactura || '';
                            const serie = f?.vSerie || '';
                            const folio = f?.vFolio || '';
                            const serieFolio = [serie, folio].filter(Boolean).join('-');
                            const fecha = f?.dFechaEmision || '';
                            const activo = f?.bIsActive === 1 || f?.bIsActive === true;
                            const tipo = f?.vTipoComprobante || '';
                            const tipoInfo = getTipoIcon(tipo);
                            const total = f?.dTotal ?? '';
                            const moneda = f?.vMoneda || '';
                            const isSelected = selectedFactura?.vUUIDFactura === uuid;

                            return (
                                <div
                                    key={idx}
                                    onClick={() => setSelectedFactura(f)}
                                    className={`p-2 border rounded shadow-sm ${isSelected ? 'border-primary' : ''}`}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease-in-out',
                                        backgroundColor: isSelected 
                                            ? (isDark ? '#1a2942' : '#f0f7ff') 
                                            : (isDark ? '#121e2d' : '#ffffff'),
                                        borderColor: isSelected 
                                            ? '#0d6efd' 
                                            : (isDark ? '#2b3035' : '#e9ecef')
                                    }}
                                >
                                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
                                        
                                        {/* Left Side: Icon & Details */}
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="d-flex align-items-center justify-content-center rounded-circle me-2 flex-shrink-0" 
                                                style={{ 
                                                    width: '32px', 
                                                    height: '32px', 
                                                    backgroundColor: isDark ? `${tipoInfo.color}15` : `${tipoInfo.color}15`, 
                                                    color: tipoInfo.color 
                                                }}
                                            >
                                                <FontAwesomeIcon icon={tipoInfo.icon} size="sm" />
                                            </div>
                                            <div className="d-flex flex-column justify-content-center">
                                                {/* Renglón 1: Serie, Folio y UUID en rojo */}
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    {serieFolio ? (
                                                        <span className="fw-bold fs--1">
                                                            <span className="text-danger">{serieFolio}</span>
                                                            <span className="ms-1" style={{ color: isSelected ? '#0d6efd' : (isDark ? '#e9ecef' : '#212529') }}>
                                                                ({uuid})
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="fw-bold fs--1" style={{ color: isSelected ? '#0d6efd' : (isDark ? '#e9ecef' : '#212529') }}>
                                                            {uuid}
                                                        </span>
                                                    )}
                                                    {!activo && <Badge bg="danger" style={{ fontSize: '0.6rem' }} pill>Cancelada</Badge>}
                                                </div>
                                                
                                                {/* Renglón 2: RFC - NOMBRE RECEPTOR */}
                                                <div className="mb-1" style={{ fontSize: '0.75rem' }}>
                                                    <strong className={isDark ? "text-light" : "text-dark"}>{f?.vRfcReceptor || 'N/A'} - {f?.vRazonSocialReceptor || f?.vNombreReceptor || ''}</strong>
                                                </div>

                                                {/* Renglón 3: Fecha y Pagos */}
                                                <div className="text-muted d-flex align-items-center flex-wrap gap-1" style={{ fontSize: '0.7rem' }}>
                                                    <span><FontAwesomeIcon icon={faFileInvoice} className="me-1 opacity-50" /> {fecha.substring(0,10)}</span>
                                                    <span className="opacity-25">&bull;</span>
                                                    <span>Pagos: <strong className={isDark ? "text-light" : "text-dark"}>{f?.iTotalPagos || 0}</strong></span>
                                                    {tipo && (
                                                        <>
                                                            <span className="opacity-25">&bull;</span>
                                                            <span>{tipoInfo.label || tipo}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Side: Financial Blocks */}
                                        <div className="d-flex flex-wrap flex-sm-nowrap gap-2 justify-content-between text-end ms-lg-auto bg-light rounded p-1 px-2" 
                                             style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2) !important' : '#f8f9fa !important', minWidth: '280px' }}>
                                            
                                            <div className="d-flex flex-column px-1 flex-grow-1">
                                                <span className="text-muted" style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: '600' }}>
                                                    Total
                                                </span>
                                                <span className="fw-semibold fs--1" style={{ color: isDark ? '#adb5bd' : '#495057' }}>
                                                    ${formatNumber(total)} <span className="text-muted fw-normal" style={{ fontSize: '0.6rem' }}>{moneda}</span>
                                                </span>
                                            </div>
                                            
                                            <div className="d-flex flex-column px-1 border-start flex-grow-1">
                                                <span className="text-muted" style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: '600' }}>
                                                    Abonado
                                                </span>
                                                <span className="fw-semibold text-success fs--1">
                                                    ${formatNumber(f?.dTotalPagado || 0)} <span className="text-muted fw-normal" style={{ fontSize: '0.6rem' }}>{moneda}</span>
                                                </span>
                                            </div>

                                            <div className="d-flex flex-column px-1 border-start flex-grow-1">
                                                <span className="text-primary" style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: '700' }}>
                                                    Restante
                                                </span>
                                                <span className="fw-bold text-primary fs--1">
                                                    ${formatNumber(Math.max(0, Number(total || 0) - Number(f?.dTotalPagado || 0)))} <span className="opacity-75 fw-normal" style={{ fontSize: '0.6rem' }}>{moneda}</span>
                                                </span>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {hasFacturas && totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination size="sm" className="mb-0">
                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                            {renderPaginationItems()}
                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Row className="w-100">
                    <Col xs={6} className="text-start">
                        <Button variant="link" onClick={onHide}>Cancelar</Button>
                    </Col>
                    <Col xs={6} className="text-end">
                        <Button variant="primary" onClick={() => onConfirm(selectedFactura)} disabled={!selectedFactura}>Aceptar</Button>
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    );
};

SelectFacturaPPDModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string,
    rfcEmisor: PropTypes.string,
    rfcReceptor: PropTypes.string
};

export default SelectFacturaPPDModal;
